import React, { useState } from 'react';
import { Paper, Box, Typography, Button, TextField, CircularProgress, Alert } from '@mui/material';
import {
  DashboardRounded, StorefrontRounded, PaidRounded, WorkspacePremiumRounded, GroupRounded,
  ViewAgendaRounded, CheckCircleRounded, PaymentRounded, CloseRounded, FileDownloadOutlined,
} from '@mui/icons-material';
import { Field, SectionCard, Grid } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import StatCard from '../../components/StatCard';
import StatusChip from '../../components/StatusChip';
import { LineChart, BarChart } from '../../components/Charts';
import { useToast } from '../../components/Toast';
import { useAdminReports } from '../../hooks/useReports';
import { money, num, utilPct, statusLabel } from '../../utils/format';
import { colors } from '../../theme/theme';

const REPORTS = [
  { key: 'overview', label: 'Overview', icon: <DashboardRounded /> },
  { key: 'business', label: 'Business Report', icon: <StorefrontRounded /> },
  { key: 'revenue', label: 'Revenue Report', icon: <PaidRounded /> },
  { key: 'subscription', label: 'Subscription Report', icon: <WorkspacePremiumRounded /> },
  { key: 'consumer', label: 'Consumer Report', icon: <GroupRounded /> },
  { key: 'queue', label: 'Queue Performance', icon: <ViewAgendaRounded /> },
  { key: 'token', label: 'Token Report', icon: <CheckCircleRounded /> },
  { key: 'payment', label: 'Payment Report', icon: <PaymentRounded /> },
];

const sum = (rows: any[], k: string) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);

const COLS: Record<string, Column<any>[]> = {
  business: [
    { key: 'provider', label: 'Business' },
    { key: 'bookings', label: 'Bookings', align: 'right', csv: (r) => r.bookings, render: (r) => num(r.bookings) },
    { key: 'revenue', label: 'Revenue', align: 'right', csv: (r) => r.revenue, render: (r) => money(r.revenue) },
  ],
  revenue: [
    { key: 'period', label: 'Period' },
    { key: 'revenue', label: 'Revenue', align: 'right', csv: (r) => r.revenue, render: (r) => money(r.revenue) },
    { key: 'txns', label: 'Transactions', align: 'right' },
  ],
  subscription: [
    { key: 'business', label: 'Business' }, { key: 'plan', label: 'Plan' }, { key: 'renewal', label: 'Renewal' },
    { key: 'status', label: 'Status', csv: (r) => r.status, render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
  ],
  consumer: [
    { key: 'name', label: 'Consumer' }, { key: 'city', label: 'City' },
    { key: 'tokens', label: 'Tokens', align: 'right' }, { key: 'lastVisit', label: 'Last Visit' },
  ],
  queue: [
    { key: 'queue', label: 'Queue' }, { key: 'business', label: 'Business' }, { key: 'queue_type', label: 'Queue Type' },
    { key: 'capacity', label: 'Capacity', align: 'right' }, { key: 'booked', label: 'Booked', align: 'right' },
    { key: 'pct', label: 'Utilization', align: 'right', csv: (r) => utilPct(r.booked, r.capacity), render: (r) => utilPct(r.booked, r.capacity) + '%' },
  ],
  token: [
    { key: 'number', label: 'Token #' }, { key: 'customer', label: 'Customer' }, { key: 'service', label: 'Service' },
    { key: 'token_type', label: 'Token Type' },
    { key: 'status', label: 'Status', csv: (r) => r.status, render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> }, { key: 'time', label: 'Time' },
  ],
  payment: [
    { key: 'id', label: 'Payment', render: (r) => <Typography component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{String(r.id).slice(0, 8)}</Typography> },
    { key: 'business', label: 'Business' },
    { key: 'amount', label: 'Amount', align: 'right', csv: (r) => r.amount, render: (r) => money(r.amount) },
    { key: 'status', label: 'Status', csv: (r) => r.status, render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
  ],
};

const AdminReportsPage: React.FC = () => {
  const toast = useToast();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const ranged = !!(from && to);
  const { data, loading, error, reload } = useAdminReports(ranged ? from : undefined, ranged ? to : undefined);
  const [key, setKey] = useState('overview');

  const ov: any = data?.overview;
  const summaryCols: Column<any>[] = [
    { key: 'category', label: 'Category' },
    { key: 'providers', label: 'Providers', align: 'right' },
    { key: 'revenue', label: 'Revenue', align: 'right', csv: (r) => r.revenue, render: (r) => money(r.revenue) },
  ];

  const kpisFor = (k: string): Array<{ icon: React.ReactElement; color: string; value: React.ReactNode; label: string }> => {
    const rows: any[] = (data?.[k] as any[]) || [];
    switch (k) {
      case 'business': return [
        { icon: <StorefrontRounded />, color: '#076FF7', value: num(rows.length), label: 'Businesses' },
        { icon: <ViewAgendaRounded />, color: '#16A34A', value: num(sum(rows, 'bookings')), label: 'Total Bookings' },
        { icon: <PaidRounded />, color: '#7C3AED', value: money(sum(rows, 'revenue')), label: 'Total Revenue' },
      ];
      case 'revenue': return [
        { icon: <PaidRounded />, color: '#16A34A', value: money(sum(rows, 'revenue')), label: 'Total Revenue' },
        { icon: <PaymentRounded />, color: '#076FF7', value: num(sum(rows, 'txns')), label: 'Transactions' },
      ];
      case 'subscription': return [
        { icon: <WorkspacePremiumRounded />, color: '#076FF7', value: num(rows.length), label: 'Subscriptions' },
        { icon: <CheckCircleRounded />, color: '#16A34A', value: num(rows.filter((r) => r.status === 'active').length), label: 'Active' },
      ];
      case 'consumer': return [
        { icon: <GroupRounded />, color: '#076FF7', value: num(rows.length), label: 'Consumers' },
        { icon: <ViewAgendaRounded />, color: '#7C3AED', value: num(sum(rows, 'tokens')), label: 'Total Tokens' },
      ];
      case 'queue': return [
        { icon: <ViewAgendaRounded />, color: '#076FF7', value: num(rows.length), label: 'Queues' },
        { icon: <CheckCircleRounded />, color: '#16A34A', value: num(sum(rows, 'booked')), label: 'Total Booked' },
      ];
      case 'token': return [
        { icon: <CheckCircleRounded />, color: '#076FF7', value: num(rows.length), label: 'Tokens (recent)' },
        { icon: <CheckCircleRounded />, color: '#16A34A', value: num(rows.filter((r) => r.status === 'completed').length), label: 'Completed' },
      ];
      case 'payment': return [
        { icon: <PaidRounded />, color: '#16A34A', value: money(rows.filter((r) => r.status === 'paid').reduce((a, r) => a + Number(r.amount || 0), 0)), label: 'Collected' },
        { icon: <PaymentRounded />, color: '#076FF7', value: num(rows.length), label: 'Records' },
      ];
      default: return [];
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="From"><TextField type="date" value={from} onChange={(e) => setFrom(e.target.value)} size="small" inputProps={{ max: to || undefined }} /></Field>
          <Field label="To"><TextField type="date" value={to} onChange={(e) => setTo(e.target.value)} size="small" inputProps={{ min: from || undefined }} /></Field>
          <Field label="&nbsp;"><Button variant="outlined" size="small" onClick={() => { setFrom(''); setTo(''); }} disabled={!from && !to} sx={{ height: 40 }}>All time</Button></Field>
        </Box>
        <Button variant="outlined" startIcon={<FileDownloadOutlined />} onClick={() => toast('Use the Export Excel button on each report table')}
          sx={{ borderColor: 'divider', color: 'text.primary' }}>Export Report</Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>
      ) : error || !data ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'No data.'}</Alert>
      ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '230px 1fr' }, gap: 2.25, alignItems: 'start' }}>
        <Paper sx={{ p: 1.5 }}>
          {REPORTS.map((r) => {
            const active = r.key === key;
            return (
              <Box key={r.key} onClick={() => setKey(r.key)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.25, borderRadius: '10px', cursor: 'pointer',
                  fontSize: 13.5, fontWeight: 600, color: active ? colors.primary : colors.textSecondary,
                  bgcolor: active ? colors.primaryLight : 'transparent', '& svg': { fontSize: 18 },
                  '&:hover': { bgcolor: active ? colors.primaryLight : colors.gray50 },
                }}>
                {r.icon} {r.label}
              </Box>
            );
          })}
        </Paper>

        <Box>
          {key === 'overview' ? (
            <>
              <Grid cols={4} sx={{ mb: 2.25 }}>
                <StatCard icon={<CheckCircleRounded />} color="#076FF7" value={num(ov.kpis.total_tokens)} label="Total Tokens" />
                <StatCard icon={<CheckCircleRounded />} color="#16A34A" value={num(ov.kpis.completed)} label="Completed" />
                <StatCard icon={<CloseRounded />} color="#EF4444" value={num(ov.kpis.cancelled)} label="Cancelled" />
                <StatCard icon={<ViewAgendaRounded />} color="#F59E0B" value={num(ov.kpis.skipped)} label="Skipped" />
              </Grid>
              <Grid cols={2} sx={{ mb: 2.25 }}>
                <SectionCard title="Tokens Trend"><LineChart labels={ov.trend.map((t: any) => t.month)} series={[{ data: ov.trend.map((t: any) => t.bookings), color: '#076FF7', fill: true }]} height={220} /></SectionCard>
                <SectionCard title="Businesses by Category"><BarChart labels={ov.by_category.map((c: any) => c.name)} data={ov.by_category.map((c: any) => c.count)} /></SectionCard>
              </Grid>
              <Paper sx={{ p: 0 }}>
                <Box sx={{ px: 2.5, pt: 2.25 }}><Typography sx={{ fontSize: 16, fontWeight: 700 }}>Report Summary (by Category)</Typography></Box>
                <DataTable columns={summaryCols} rows={ov.summary} exportName="report-summary" />
              </Paper>
            </>
          ) : (
            <>
              <Grid cols={3} sx={{ mb: 2.25 }}>
                {kpisFor(key).map((k, i) => <StatCard key={i} {...k} />)}
              </Grid>
              <Paper sx={{ p: 0 }}>
                <DataTable columns={COLS[key]} rows={data[key] || []} title={REPORTS.find((r) => r.key === key)!.label} exportName={`${key}-report`} />
              </Paper>
            </>
          )}
        </Box>
      </Box>
      )}
    </>
  );
};

export default AdminReportsPage;
